import Vue from 'vue'
import i18n from '@vue-storefront/i18n'
import toString from 'lodash-es/toString'
import fetch from 'isomorphic-fetch'
import * as localForage from 'localforage'
import rootStore from '@vue-storefront/core/store'
import { adjustMultistoreApiUrl, currentStoreView } from '@vue-storefront/core/lib/multistore'
import Task from '@vue-storefront/core/lib/sync/types/Task'
import { Logger } from '@vue-storefront/core/lib/logger'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import * as entities from '@vue-storefront/core/store/lib/entities'
import UniversalStorage from '@vue-storefront/core/store/lib/storage'
import { processURLAddress } from '@vue-storefront/core/helpers'
import { serial } from '@vue-storefront/core/helpers'
import config from 'config'
import { customerAuthHeaders, isDefinitiveRefreshFailure } from '@vue-storefront/core/modules/user/helpers/customerSession'
import { onlineHelper, timezoneTime } from '@vue-storefront/core/helpers'
import moment from 'moment-timezone'
import momentTime from "moment-timezone";

export function _prepareTask (task) {
  const taskId = entities.uniqueEntityId(task) // timestamp as a order id is not the best we can do but it's enough
  task.task_id = taskId.toString()
  task.transmited = false
  task.created_at = timezoneTime()
  task.updated_at = timezoneTime()
  return task
}

/**
 * K2-3138: a 401 refreshes once through user/refresh (one request shared with customerRequest and
 * other tasks) and retries with the new token. Only a rejected refresh token logs out; a 5xx or
 * network failure keeps the session and answers the task with an error.
 */
function _refreshAndRetry (resolve, reject, task: Task, failedToken, currentCartId) {
  Promise.resolve(rootStore.dispatch('user/refresh', { failedToken }))
    .then(resp => _internalExecute(resolve, reject, task, resp.result, currentCartId, true))
    .catch(err => {
      if (!isDefinitiveRefreshFailure(err)) {
        Logger.error('Could not refresh the user token, the session is kept: ' + (err && err.code), 'sync')()
        resolve({ code: err && err.code ? err.code : 503, result: i18n.t('Something went wrong. Try again in a few seconds.') })
        return
      }
      Logger.error('The refresh token was rejected, logging out', 'sync')()
      rootStore.state.userTokenInvalidateLock = -1 // later tasks wait for a new login (reset in user/login)
      rootStore.dispatch('user/logout', { silent: true })
      Vue.prototype.$bus.$emit('modal-show', 'modal-signup')
      TaskQueue.clearNotTransmited()
      resolve({ code: 401, message: i18n.t('Error refreshing user token. User is not authorized to access the resource') })
    })
}

function _internalExecute (resolve, reject, task: Task, currentToken, currentCartId, refreshed = false) {
  if (rootStore.state.userTokenInvalidateLock < 0) {
    Logger.error('Aborting the network task' + task.url + rootStore.state.userTokenInvalidateLock, 'sync')()
    resolve({ code: 401, message: i18n.t('Error refreshing user token. User is not authorized to access the resource') })
    return
  }
  const isCartIdRequired = task.url.includes('{{cartId}}') // this is bypass for #2592
  if (isCartIdRequired && !currentCartId) { // by some reason we does't have the  cart id yet
    reject('Error executing sync task ' + task.url + ' the required cartId  argument is null. Re-creating shopping cart synchro.')
    return
  }
  // K2-3138: the token goes as a Bearer header (proxies log URLs); a queued task's old {{token}} is left empty
  let url = task.url.replace('{{token}}', '').replace('{{cartId}}', (currentCartId == null) ? '' : currentCartId)
  url = processURLAddress(url); // use relative url paths
  if (config.storeViews.multistore) {
    url = adjustMultistoreApiUrl(url)
  }
  // a copy: the task (maybe stored in IndexedDB) never holds the token
  const payload = Object.assign({}, task.payload, {
    headers: Object.assign({}, task.payload && task.payload.headers, customerAuthHeaders(currentToken))
  })
  let silentMode = false
  Logger.info('Executing sync task ' + url, 'sync', task)()
  return fetch(url, payload)
    .then((response) => {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return response.json()
      } else {
        const msg = i18n.t('Error with response - bad content-type!')
        Logger.error(msg.toString(), 'sync')()
        reject(msg)
      }
    })
    .then((jsonResponse) => {
      if (jsonResponse) {
        const responseCode = parseInt(jsonResponse.code)
        if (responseCode !== 200) {
          if (responseCode === 401 /** unauthorized */ && currentToken && !refreshed) { // the token is no longer valid, try to refresh it
            Logger.error('Invalid token - need to be revalidated ' + task.url, 'sync')()
            silentMode = true
            if (config.users.autoRefreshTokens) {
              _refreshAndRetry(resolve, reject, task, currentToken, currentCartId)
              return
            }
            Logger.info('Invalidation process is disabled (autoRefreshTokens is set to false)', 'sync')()
            rootStore.dispatch('user/logout', { silent: true })
            Vue.prototype.$bus.$emit('modal-show', 'modal-signup')
          }

          if (!task.silent && jsonResponse.result && (typeof jsonResponse.result === 'string' || (((jsonResponse.result.result || jsonResponse.result.message) && jsonResponse.result.code !== 'ENOTFOUND') && !silentMode))) {
            const message = typeof jsonResponse.result === 'string' ? jsonResponse.result : typeof jsonResponse.result.result === 'string' ? jsonResponse.result.result : jsonResponse.result.message

            rootStore.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t(message),
              action1: { label: i18n.t('OK') }
            })
          }
        }

        Logger.debug('Response for: ' + task.task_id + ' = ' + JSON.stringify(jsonResponse.result), 'sync')()
        task.transmited = true
        task.transmited_at = new Date(moment().tz('Europe/Kiev').format())
        task.result = jsonResponse.result
        task.resultCode = jsonResponse.code
        task.code = jsonResponse.code // backward compatibility to fetch()
        task.acknowledged = false

        if (task.callback_event) {
          if (task.callback_event.startsWith('store:')) {
            rootStore.dispatch(task.callback_event.split(':')[1], task)
          } else {
            Vue.prototype.$bus.$emit(task.callback_event, task)
          }
        }
        resolve(task)
      } else {
        const msg = i18n.t('Unhandled error, wrong response format!')
        Logger.error(msg.toString(), 'sync')()
        reject(msg)
      }
    })
    .catch((err) => {
      Logger.error(err, 'sync')()
      reject(err)
    })
}

export function execute (task: Task, currentToken = null, currentCartId = null): Promise<Task> {
  const taskId = task.task_id

  return new Promise((resolve, reject) => {
    _internalExecute(resolve, reject, task, currentToken, currentCartId)
  })
}

export function initializeSyncTaskStorage () {
  const storeView = currentStoreView()
  const dbNamePrefix = storeView.storeCode ? storeView.storeCode + '-' : ''

  Vue.prototype.$db.syncTaskCollection = new UniversalStorage(localForage.createInstance({
    name: dbNamePrefix + 'shop',
    storeName: 'syncTasks',
    driver: localForage[config.localForage.defaultDrivers['syncTasks']]
  }))
}

export function registerSyncTaskProcessor () {
  const mutex = {}
  Vue.prototype.$bus.$on('sync/PROCESS_QUEUE', async data => {
    if (onlineHelper.isOnline) {
      // event.data.config - configuration, endpoints etc
      const syncTaskCollection = Vue.prototype.$db.syncTaskCollection
      const currentUserToken = rootStore.getters['user/getUserToken']
      const currentCartToken = rootStore.getters['cart/getCartToken']

      const fetchQueue = []
      Logger.debug('Current User token = ' + currentUserToken)()
      Logger.debug('Current Cart token = ' + currentCartToken)()
      syncTaskCollection.iterate((task, id) => {
        if (task && !task.transmited && !mutex[id]) { // not sent to the server yet
          mutex[id] = true // mark this task as being processed
          fetchQueue.push(execute(task, currentUserToken, currentCartToken).then(executedTask => {
            if (!executedTask.is_result_cacheable) {
              syncTaskCollection.removeItem(id) // remove successfully executed task from the queue
            } else {
              syncTaskCollection.setItem(id, executedTask) // update the 'transmitted' field
            }
            mutex[id] = false
          }).catch(err => {
            mutex[id] = false
            Logger.error(err)()
          }))
        }
      }, (err) => {
        if (err) Logger.error(err)()
        Logger.debug('Iteration has completed')()
        // execute them serially
        serial(fetchQueue)
        Logger.debug('Processing sync tasks queue has finished')()
      })
    }
  })
}
